"use client";

import { useState, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import { Button, Icon } from "@/components/ui";
import {
  SSO_LOGIN_TEXT,
  getMicrosoftLoginUrl,
} from "@/lib/auth/constants";

function MicrosoftIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 23 23" fill="none" aria-hidden>
      <path d="M11 0H0v11h11V0z" fill="#F25022" />
      <path d="M23 0H12v11h11V0z" fill="#7FBA00" />
      <path d="M11 12H0v11h11V12z" fill="#00A4EF" />
      <path d="M23 12H12v11h11V12z" fill="#FFB900" />
    </svg>
  );
}

function LoginContent() {
  const [error, setError] = useState<string | null>(null);
  const searchParams = useSearchParams();

  const handleSignIn = () => {
    try {
      setError(null);
      const returnTo = searchParams.get("returnTo");
      if (returnTo) {
        sessionStorage.setItem("returnTo", returnTo);
      } else {
        sessionStorage.removeItem("returnTo");
      }
      window.location.href = getMicrosoftLoginUrl();
    } catch (err) {
      setError(err instanceof Error ? err.message : "SSO is not configured");
    }
  };

  return (
    <main className="grid min-h-screen md:grid-cols-2">
      <section
        aria-label="Hero"
        className="hidden items-center justify-center bg-secondary p-12 md:flex"
      >
        <div className="max-w-md text-card">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-card/60">
            Westay
          </p>
          <h1 className="mt-3 font-display text-4xl">Catalogue Portal</h1>
          <p className="mt-3 text-sm text-card/70">
            {SSO_LOGIN_TEXT.DESCRIPTION}
          </p>
        </div>
      </section>

      <section className="flex items-center justify-center bg-bg p-8">
        <div className="card w-full max-w-md space-y-6 p-8">
          <header>
            <h1 className="font-display text-3xl text-secondary">
              {SSO_LOGIN_TEXT.TITLE}
            </h1>
            <p className="mt-2 text-sm text-muted">
              {SSO_LOGIN_TEXT.DESCRIPTION}
            </p>
          </header>

          <Button
            type="button"
            variant="outline"
            size="lg"
            className="w-full border-secondary/20 bg-card hover:bg-secondary/5"
            onClick={handleSignIn}
          >
            <MicrosoftIcon />
            {SSO_LOGIN_TEXT.BUTTON_LABEL}
          </Button>

          {error ? (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          ) : null}

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-secondary/10" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-card px-3 text-muted">
                {SSO_LOGIN_TEXT.SECURE_AUTH_TEXT}
              </span>
            </div>
          </div>

          <div className="rounded-xl border border-primary/15 bg-primary/5 p-4">
            <div className="flex gap-3">
              <Icon name="info" size={18} className="shrink-0 text-primary" />
              <div>
                <p className="text-sm font-medium text-secondary">
                  {SSO_LOGIN_TEXT.MS_ACCOUNT_REQUIRED.TITLE}
                </p>
                <p className="mt-1 text-sm text-muted">
                  {SSO_LOGIN_TEXT.MS_ACCOUNT_REQUIRED.DESCRIPTION}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

/**
 * Staff login — Microsoft SSO only (same authzilla flow as westay-panorama).
 */
export default function LoginPage() {
  return (
    <Suspense fallback={null}>
      <LoginContent />
    </Suspense>
  );
}
